import Alert from "@cloudscape-design/components/alert"
import Button from "@cloudscape-design/components/button"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useRef, useState, useMemo } from "react"
import { useForm, useFieldArray, useWatch } from "react-hook-form"
import type { InlineDiffResult } from "../../../utils/inlineDiff"

import type { DataQualityRules, DQRule } from "@/generated/sdk"
import type { DTADocumentContent } from "../../../types/dta"
import {
  hasPendingTranslation,
  useDataQualitySuggestions,
  useTranslationStatus,
} from "../../../hooks/queries/useDataQuality"
import { DTADetails } from "../../../services/dtaApi"
import { CollapsibleSection } from "./CollapsibleSection"
import { ConformanceSection } from "./ConformanceSection"
import { DEFAULT_FORM_VALUES } from "./constants"
import { DerivedRulesSection } from "./DerivedRulesSection"
import { deriveRulesFromMetadata } from "./deriveRulesFromMetadata"
import { filterDuplicateSuggestions, SuggestionsData } from "./filterDuplicateSuggestions"
import { PreConformanceSection } from "./PreConformanceSection"
import { dataQualityFormSchema } from "./schema"
import { SuggestionsReviewModal } from "./SuggestionsReviewModal"
import { TableConfigSection } from "./TableConfigSection"
import { parseThreshold } from "./threshold"
import { DataQualityFormData, EdcSourceRule } from "./types"
import { DqdlRule } from "./types"
import { useMetadataFields } from "./useMetadataFields"

/**
 * Props for the DataQualityTab component. Follows the TabContentProps interface used by DTAEditor.
 */
interface TabContentProps {
  /**
   * The DTA being edited.
   */
  dta: DTADetails

  /**
   * Current document content (data_quality_rules data is stored here)
   */
  content: DTADocumentContent

  /**
   * Callback to update document content.
   */
  onUpdate: (updates: Partial<DTADocumentContent>) => void

  /**
   * Whether the form is in edit mode.
   */
  canEdit: boolean

  /**
   * Whether an approved version has fixed the table name.
   */
  tableNameLocked?: boolean

  /**
   * Validation error returned by the save API for the table name.
   */
  serverTableNameError?: string

  /**
   * Clears a server-side table-name error after the author changes the value.
   */
  onTableNameChange?: () => void

  /**
   * Callback when validation state changes.
   */
  onValidationChange?: (isValid: boolean, errors?: Record<string, unknown>) => void

  /**
   * Callback to navigate to the Metadata tab.
   */
  onNavigateToMetadata?: () => void
  inlineDiff?: InlineDiffResult | null
}

/**
 * The form models `threshold` as a string (the percent input needs an empty-string state) but the
 * API contract is a number. Convert on load/save so the editor stays string-based while the wire
 * body conforms.
 */
export function rulesFromApi(rules: DqdlRule[] | undefined): DqdlRule[] {
  if (!Array.isArray(rules)) return []
  return rules.map((r) => (r.threshold == null ? r : { ...r, threshold: String(r.threshold) }))
}

/**
 * Convert form rules to the wire shape (threshold string -> number). Invalid thresholds are
 * dropped; this is only safe because save is gated on schema validity (onValidationChange) so a
 * rule mid-edit with a bad threshold never actually persists stripped.
 */
export function rulesToApi(rules: DqdlRule[] | undefined): DQRule[] {
  if (!Array.isArray(rules)) return []
  return rules.map((r) => {
    // _added is a UI-only marker (set by filterDuplicateSuggestions); never send it on the wire.
    const { threshold, _added: _drop, ...rest } = r
    const parsed = parseThreshold(threshold)
    return parsed.ok ? { ...rest, threshold: parsed.value } : rest
  })
}

/**
 * Project the full form state to the wire `DataQualityRules` shape by naming every field
 * explicitly. Both this object and `DQRule` are `additionalProperties: false`, so a spread of the
 * form would let any future form-only field become a silent 400 - listing fields makes the boundary
 * safe by construction.
 */
function formToApi(values: DataQualityFormData): DataQualityRules {
  return {
    fileMatching: values.fileMatching,
    preConformance: values.preConformance,
    majorRules: rulesToApi(values.majorRules),
    minorRules: rulesToApi(values.minorRules),
    majorPii: values.majorPii,
    minorPii: values.minorPii,
    table: values.table,
  }
}

/**
 * Normalize API data to form format (arrays to comma-separated strings)
 */
function normalizeFormData(data: unknown): DataQualityFormData | undefined {
  if (!data || typeof data !== "object") return undefined

  const d = data as Partial<DataQualityFormData>
  const result: DataQualityFormData = {
    fileMatching: d.fileMatching ?? DEFAULT_FORM_VALUES.fileMatching,
    preConformance: d.preConformance ?? DEFAULT_FORM_VALUES.preConformance,
    majorRules: rulesFromApi(d.majorRules),
    majorPii: d.majorPii ?? DEFAULT_FORM_VALUES.majorPii,
    minorRules: rulesFromApi(d.minorRules),
    minorPii: d.minorPii ?? DEFAULT_FORM_VALUES.minorPii,
    table: d.table ?? DEFAULT_FORM_VALUES.table,
  }

  if (result.preConformance.files && Array.isArray(result.preConformance.files)) {
    result.preConformance.files = result.preConformance.files.map((f) => ({
      ...f,
      allowedExtensions: Array.isArray(f.allowedExtensions)
        ? f.allowedExtensions.join(", ")
        : f.allowedExtensions,
    }))
  }

  return result
}

/**
 * Tab component for configuring Data Quality Rules within the DTA Editor. Allows DAEs to define
 * file validation rules, DQDL conformance checks, PII detection fields, and Iceberg table
 * configuration.
 */
export const DataQualityTab: React.FC<TabContentProps> = ({
  dta,
  content,
  onUpdate,
  canEdit,
  tableNameLocked = false,
  serverTableNameError,
  onTableNameChange,
  onValidationChange,
  onNavigateToMetadata,
  inlineDiff,
}) => {
  const existingData = normalizeFormData(content.data_quality_rules)
  const availableFields = useMetadataFields(content)

  const {
    register,
    control,
    watch,
    getValues,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm<DataQualityFormData>({
    defaultValues: existingData || DEFAULT_FORM_VALUES,
    resolver: zodResolver(dataQualityFormSchema),
    mode: "all",
  })

  const fileFields = useFieldArray({ control, name: "preConformance.files" })
  const majorRuleFields = useFieldArray({ control, name: "majorRules" })
  const minorRuleFields = useFieldArray({ control, name: "minorRules" })

  // Derive rules from metadata variables
  const derivedRules = useMemo(() => {
    return deriveRulesFromMetadata(content.transfer_metadata_schema?.variables)
  }, [content.transfer_metadata_schema])

  // Watch form rules for reactive deduplication of suggestions
  const watchedMajorRules = useWatch({ control, name: "majorRules" })
  const watchedMinorRules = useWatch({ control, name: "minorRules" })

  const onUpdateRef = useRef(onUpdate)
  onUpdateRef.current = onUpdate

  const initializedRef = useRef(false)

  const [showReviewModal, setShowReviewModal] = useState(false)

  // Suggestions fetch + generating-poll (stops at terminal status or the attempt cap) via TanStack
  // Query. The hook owns polling; the presentation status/message/suggestions are derived from its
  // state below, so there's no local mirror to keep in sync.
  const {
    data: suggestionsResponse,
    isLoading: suggestionsLoading,
    isError: suggestionsError,
    fetchStatus: suggestionsFetchStatus,
    refetch: refetchSuggestions,
  } = useDataQualitySuggestions(dta.dta_id, canEdit)

  const { suggestions, suggestionsStatus, suggestionsMessage } = useMemo((): {
    suggestions: DataQualityFormData | null
    suggestionsStatus: "ready" | "generating" | "unavailable" | "error" | "loading"
    suggestionsMessage: string
  } => {
    if (!canEdit)
      return { suggestions: null, suggestionsStatus: "unavailable", suggestionsMessage: "" }
    if (suggestionsLoading)
      return { suggestions: null, suggestionsStatus: "loading", suggestionsMessage: "" }
    if (suggestionsError || !suggestionsResponse) {
      return {
        suggestions: null,
        suggestionsStatus: "error",
        suggestionsMessage: "Failed to fetch suggestions",
      }
    }
    const message = suggestionsResponse.message || ""
    if (suggestionsResponse.status === "ready" && suggestionsResponse.suggestions) {
      return {
        suggestions: suggestionsResponse.suggestions as DataQualityFormData,
        suggestionsStatus: "ready",
        suggestionsMessage: message,
      }
    }
    if (suggestionsResponse.status === "ready") {
      return {
        suggestions: null,
        suggestionsStatus: "unavailable",
        suggestionsMessage: "No suggestions available for these fields",
      }
    }
    if (suggestionsResponse.status === "generating") {
      // The hook stops the poll (refetchInterval -> false) once the attempt cap is hit, so an idle
      // fetch status while still "generating" means we timed out; surface that, not a stuck spinner.
      return suggestionsFetchStatus === "idle"
        ? {
            suggestions: null,
            suggestionsStatus: "unavailable",
            suggestionsMessage: "Timed out waiting for suggestions",
          }
        : { suggestions: null, suggestionsStatus: "generating", suggestionsMessage: message }
    }
    return { suggestions: null, suggestionsStatus: "unavailable", suggestionsMessage: message }
  }, [canEdit, suggestionsResponse, suggestionsLoading, suggestionsError, suggestionsFetchStatus])

  useEffect(() => {
    if (!initializedRef.current && existingData) {
      // Ensure at least one file rule exists
      const dataToReset = { ...existingData }
      if (!dataToReset.preConformance?.files?.length) {
        dataToReset.preConformance = {
          ...dataToReset.preConformance,
          files: DEFAULT_FORM_VALUES.preConformance.files,
        }
      }
      reset(dataToReset)
      initializedRef.current = true
    }
  }, [existingData, reset])

  // Pre-fill PK fields from derived metadata if empty
  const derivedPrimaryKey = useMemo(
    () => derivedRules.find((r) => r.type === "IsPrimaryKey"),
    [derivedRules],
  )
  useEffect(() => {
    const currentPk = watch("table.primaryKeyFields")
    if ((!currentPk || currentPk.length === 0) && derivedPrimaryKey?.columns) {
      setValue("table.primaryKeyFields", derivedPrimaryKey.columns, {
        shouldDirty: false,
      })
    }
  }, [derivedPrimaryKey, watch, setValue])

  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0
    onValidationChange?.(!hasErrors, errors as Record<string, unknown>)
  }, [errors, onValidationChange])

  const isMounted = useRef(false)

  useEffect(() => {
    // Activate after first render cycle so form initialization doesn't trigger onUpdate
    const timer = setTimeout(() => {
      isMounted.current = true
    }, 0)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const subscription = watch(() => {
      if (!isMounted.current) return
      // Build the wire payload from a single typed getValues() snapshot via an explicit projection
      // (not a spread) so a form-only field can't silently leak past additionalProperties: false.
      onUpdateRef.current({ data_quality_rules: formToApi(getValues()) })
      void trigger()
    })
    return () => subscription.unsubscribe()
  }, [watch, getValues, trigger])

  // EDC rule translation polling
  const pendingTranslationRules = useMemo(
    () => (content._edc_source_rules as EdcSourceRule[] | undefined) || [],
    [content._edc_source_rules],
  )
  const hasTranslationPending = pendingTranslationRules.length > 0

  const pendingMajorRules = useMemo(
    () => pendingTranslationRules.filter((r) => r.severity === "major"),
    [pendingTranslationRules],
  )
  const pendingMinorRules = useMemo(
    () => pendingTranslationRules.filter((r) => r.severity === "minor"),
    [pendingTranslationRules],
  )

  // Poll the DTA detail while translation is pending; the hook stops once _edc_source_rules clears.
  const { data: translationDta } = useTranslationStatus(dta.dta_id, hasTranslationPending)

  useEffect(() => {
    if (!hasTranslationPending || !translationDta) return
    if (hasPendingTranslation(translationDta)) return

    // Translation complete - append the new SDS rules to current form state without discarding any
    // unsaved edits the DAE may have made while waiting.
    const freshContent = translationDta.document_content || {}
    const freshDq = (freshContent.data_quality_rules || {}) as Record<string, unknown>
    const newMajor = ((freshDq.majorRules || []) as DqdlRule[]).filter((r) => r.source === "sds")
    const newMinor = ((freshDq.minorRules || []) as DqdlRule[]).filter((r) => r.source === "sds")

    const currentMajor = watch("majorRules") || []
    const currentMinor = watch("minorRules") || []
    if (newMajor.length > 0) {
      setValue("majorRules", [...currentMajor, ...newMajor], { shouldDirty: true })
    }
    if (newMinor.length > 0) {
      setValue("minorRules", [...currentMinor, ...newMinor], { shouldDirty: true })
    }

    // Clear _edc_source_rules from parent content to stop the pending state. Only clear this key -
    // don't overwrite data_quality_rules since the form owns that state and the backend version may
    // lack UI-only fields.
    onUpdateRef.current({ _edc_source_rules: undefined })
  }, [hasTranslationPending, translationDta, watch, setValue])

  // Filter suggestions reactively against derived rules and current form state
  const filteredSuggestions = useMemo((): SuggestionsData | null => {
    if (!suggestions) return null
    const rawSuggestions: SuggestionsData = {
      majorRules: suggestions.majorRules || [],
      minorRules: suggestions.minorRules || [],
    }
    const allFormRules = [...(watchedMajorRules || []), ...(watchedMinorRules || [])]
    return filterDuplicateSuggestions(rawSuggestions, derivedRules, allFormRules)
  }, [suggestions, derivedRules, watchedMajorRules, watchedMinorRules])

  const hasFilteredSuggestions =
    filteredSuggestions !== null &&
    (filteredSuggestions.majorRules.length > 0 || filteredSuggestions.minorRules.length > 0)

  const handleApplySuggestions = (majorRules: DqdlRule[], minorRules: DqdlRule[]) => {
    // Append selected rules to existing rules
    const currentMajor = watch("majorRules") || []
    const currentMinor = watch("minorRules") || []

    setValue("majorRules", [...currentMajor, ...majorRules], { shouldDirty: true })
    setValue("minorRules", [...currentMinor, ...minorRules], { shouldDirty: true })

    setShowReviewModal(false)
    // Re-fetch to update "already added" statuses
    void refetchSuggestions()
  }

  return (
    <SpaceBetween size="l">
      {/* EDC Rule Translation Banner */}
      {hasTranslationPending && (
        <Alert type="info">
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Spinner />
            <span>
              Translating {pendingTranslationRules.length} validation rules into data quality
              checks...
            </span>
          </SpaceBetween>
        </Alert>
      )}

      {/* Suggestions Banner */}
      {(suggestionsStatus === "loading" || suggestionsStatus === "generating") && (
        <Alert type="info">
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Spinner />
            <span>
              {suggestionsStatus === "loading"
                ? "Checking for suggestions..."
                : "Analyzing Data Transfer Agreement to suggest validation rules..."}
            </span>
          </SpaceBetween>
        </Alert>
      )}

      {suggestionsStatus === "ready" && suggestions && hasFilteredSuggestions && (
        <Alert
          type="success"
          action={
            <Button disabled={!canEdit} onClick={() => setShowReviewModal(true)}>
              Review Suggestions
            </Button>
          }
        >
          AI-generated suggestions are available for this DTA.
        </Alert>
      )}

      {suggestionsStatus === "ready" && suggestions && !hasFilteredSuggestions && (
        <Alert type="info">
          AI analysis found no additional rules beyond your current configuration.
        </Alert>
      )}

      {suggestionsStatus === "unavailable" && (
        <Alert type="info">{suggestionsMessage || "No suggestions available for this DTA"}</Alert>
      )}

      {suggestionsStatus === "error" && (
        <Alert type="error">{suggestionsMessage || "Failed to load suggestions"}</Alert>
      )}

      <SpaceBetween size="l">
        <PreConformanceSection
          watch={watch}
          setValue={setValue}
          fileFields={fileFields}
          canEdit={canEdit}
          errors={errors}
        />

        {/* Conformance Rules */}
        <CollapsibleSection
          title="Conformance Rules"
          description="Data quality checks run against each row in the uploaded file"
          commentAnchor="Data Quality Rules / Conformance"
        >
          <SpaceBetween size="m">
            <DerivedRulesSection
              derivedRules={derivedRules}
              onNavigateToMetadata={onNavigateToMetadata}
            />

            <ConformanceSection
              title="Major Rules"
              description="If any row fails these checks, the entire upload is quarantined"
              fields={majorRuleFields.fields}
              append={majorRuleFields.append}
              remove={majorRuleFields.remove}
              register={register}
              watch={watch}
              setValue={setValue}
              prefix="majorRules"
              piiPrefix="majorPii"
              canEdit={canEdit}
              errors={errors}
              availableFields={availableFields}
              onNavigateToMetadata={onNavigateToMetadata}
              pendingRules={pendingMajorRules}
              inlineDiff={inlineDiff?.dqRules}
              dtaId={dta.dta_id}
              version={dta.version}
            />

            <ConformanceSection
              title="Minor Rules"
              description="If any row fails these checks, the upload may be quarantined depending on severity"
              fields={minorRuleFields.fields}
              append={minorRuleFields.append}
              remove={minorRuleFields.remove}
              register={register}
              watch={watch}
              setValue={setValue}
              prefix="minorRules"
              piiPrefix="minorPii"
              canEdit={canEdit}
              errors={errors}
              availableFields={availableFields}
              onNavigateToMetadata={onNavigateToMetadata}
              pendingRules={pendingMinorRules}
              inlineDiff={inlineDiff?.dqRules}
              dtaId={dta.dta_id}
              version={dta.version}
            />
          </SpaceBetween>
        </CollapsibleSection>

        <TableConfigSection
          setValue={setValue}
          watch={watch}
          canEdit={canEdit}
          tableNameLocked={tableNameLocked}
          serverTableNameError={serverTableNameError}
          onTableNameChange={onTableNameChange}
          errors={errors}
          availableFields={availableFields}
          onNavigateToMetadata={onNavigateToMetadata}
        />
      </SpaceBetween>

      {/* Suggestions Review Modal */}
      {showReviewModal && filteredSuggestions && (
        <SuggestionsReviewModal
          suggestions={filteredSuggestions}
          onApply={handleApplySuggestions}
          onCancel={() => setShowReviewModal(false)}
        />
      )}
    </SpaceBetween>
  )
}
